import Section from '../../components/layout/Section/Section';
import { Box, Button, Container, Link, Typography } from '@mui/material';
import ContentHeader from '../../components/layout/ContentHeader/ContentHeader';
import { PAGE } from '../../constants/page';
import NavBreadcrumbs from '../../components/navigation/NavBreadcrumbs/NavBreadcrumbs';
import ContentTitle from '../../components/layout/ContentTitle/ContentTitle';
import Hr from '../../components/ui/Hr/Hr';
import OtherSection from '../../components/display/OtherSection/OtherSection';
import { Download } from '@mui/icons-material';
import Flow from '../../components/display/Flow/Flow';

interface Props {
  children?: React.ReactElement<unknown>;
}

const flows: Array<Flow> = [
  {
    step: "1",
    title: "経営者の責任",
    contents: [
      <>当社は、情報セキュリティの確保を経営の最重要課題の一つと位置付け、<br />経営陣が率先してリスク評価と対策の実施を推進し、組織全体の継続的な改善に努めます。</>
    ]
  },
  {
    step: "2",
    title: "情報セキュリティ体制の整備",
    contents: [
      <>当社は、情報セキュリティ委員会などの専任体制を構築し、セキュリティポリシーや開発・運用に関する社内ルールを整備・運用します。<br />また、クラウド環境、外部委託、OSSの活用等を含む全開発プロセスにおいてリスク管理を徹底します。</>
    ]
  },
  {
    step: "3",
    title: "従業員の責任と教育",
    contents: [
      <>すべての従業員は、情報資産の重要性を理解し、セキュリティ意識を高く保つよう努めます。<br />当社は、開発者・運用者を含む従業員に対し、セキュアコーディング、脆弱性対応、個人情報保護などの教育・訓練を定期的に実施します。</>
    ]
  },
  {
    step: "4",
    title: "法令・契約・業界標準の遵守",
    contents: [
      <>当社は、個人情報保護法、不正アクセス禁止法、著作権法などの関連法令および契約上の義務を遵守します。<br />また、ISMSなどの業界標準・ガイドラインに準拠し、顧客や取引先との信頼関係を維持・強化します。</>
    ]
  },
  {
    step: "5",
    title: "インシデント対応と再発防止",
    contents: [
      <>当社は、情報セキュリティに関する事故やインシデントが発生した際には、迅速かつ適切に対応し、被害の最小化と影響範囲の特定を図ります。<br />再発防止策を講じ、原因分析と仕組みの見直しを通じて継続的な改善に取り組みます。</>
    ]
  },
  {
    step: "6",
    title: "見直しと継続的改善",
    contents: [
      <>当社は、本方針を取り巻く環境の変化や技術動向、法令改正に対応するため、<br />定期的に本方針を見直し、情報セキュリティマネジメントの有効性向上に努めます。</>
    ]
  },
]

export default function Security(props: Props) {
  return (
    <>
      <Section color="dark">
        <ContentHeader title={PAGE.SECURITY.NAME} subTitle={PAGE.SECURITY.TEXT} />
      </Section>

      <Section color="light" padding="1rem 0">
        <Container>
          <NavBreadcrumbs />
        </Container>
      </Section>
      <Section color='light'>
        <Container>
          <ContentTitle title={'POLICY'} subTitle={'情報セキュリティへの取り組み'} />
        </Container>
      </Section>

      <Section color="light" padding="1rem 0">
        <Container>
          <Typography
            variant="h3"
            textAlign={'center'}
            sx={(theme) => ({
              marginTop: '3rem',
              [theme.breakpoints.up('xs')]: {
                fontSize: '1.5rem',
              },
              [theme.breakpoints.up('md')]: {
                fontSize: '2rem',
              },
              [theme.breakpoints.up('lg')]: {
                fontSize: '2.5rem',
              }
            })}
          >
            情報セキュリティ基本方針
          </Typography>

          <Hr />

          <Typography variant="body1" margin={'3rem 0'}>
            株式会社サプライム（以下、当社という）は、以下の通り企業理念を定め、コンピュータソフトウェア並びにコンピュータシステムの受託開発、技術者派遣、<br />
            コンピュータ社会に広く用いられるパッケージ開発、システム・コンサルテーションなどの企業活動を通じて、お客様からの「信頼と満足」を獲得してまいります。
          </Typography>

          <Box
            sx={(theme) => ({
              position: 'relative',
              margin: '1rem 0'
            })}>
            <Typography
              variant="body1"
              sx={(theme) => ({
                position: 'absolute',
                top: '-0.75em',
                left: '50%',
                transform: 'translateX(-50%)',
                background: theme.palette.grey[50],
                padding: '0 0.5em',
                fontWeight: 'bold',
                fontSize: '1.5rem'
              })}
            >
              企業理念
            </Typography>
            <Typography
              variant="body1"
              sx={(theme) => ({
                border: '5px',
                borderStyle: 'solid',
                borderColor: theme.palette.primary.main,
                padding: '2rem',
                textAlign: 'center',
                fontSize: '1.5rem',
                fontWeight: "bold"
              })}>
              自由な発想で新たな領域に踏み込み、<br />
              IT を通して未来と新たな価値を創造し、社会に貢献する。
            </Typography>
          </Box>

          <Typography variant="body1" margin={'3rem 0'}>
            これらのサービス提供においては、お客様の先端技術情報、個人情報など、多様な情報を取り扱う社会的責任が伴います。<br />
            関係するすべての情報ならびにコンピュータシステム（以下、情報資産という）のセキュリティ確保が必要不可欠です。<br />
            当社は、情報資産を事故・災害・犯罪などの脅威から守り、お客様ならびに社会の信頼に応えるべく、<br />
            以下の方針に基づき全社で情報セキュリティに取り組んでまいります。
          </Typography>

          <Flow flows={flows} noLine />

          <Typography variant="body1" marginTop={'3rem'} textAlign={'right'}>
            以上<br /><br />
            制定日：2025 年 5 月 21 日<br /><br />
            株式会社サプライム<br />
            代表取締役 石川 美樹
          </Typography>

          <Box textAlign={'center'} margin={'3rem 0'}>
            <Link href={'/assets/files/security_policy_suprime.pdf'} target={'_blank'} rel={'noreferrer'}>
              <Button
                size='large'
                color='secondary'
                variant="outlined"
                endIcon={<Download />}
              >
                <Typography variant="body1">
                  基本方針（PDF）をダウンロード
                </Typography>
              </Button>
            </Link>
          </Box>
        </Container >
      </Section >

      <Hr />

      <Section color='dark' height={'300px'} backgroundImage='url(/assets/images/top.jpg)' isBackgroundCover>
        <Container>
          <OtherSection isObserver={false} />
        </Container>
      </Section>
    </>
  )
}